import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { combineAll } from '@datachain/ui-sdk/common';
import { AppModalService } from '@datachain/ui-sdk/components/app-modal';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
	catchError,
	combineLatest,
	filter,
	map,
	of,
	switchMap,
	tap,
	withLatestFrom,
} from 'rxjs';
import { TagAdapter } from 'src/app/core/tag.adapter';
import { IDcTag } from 'src/app/requesters/core/models/tag.model';
import { TagRequester } from 'src/app/requesters/core/tag.requester';

import { NgCommService } from '../../ajs-ng-communication.service';
import { AjsScopeService } from '../../ajs-scope.service';
import { AjsStateService } from '../../ajs-state.service';
import { states } from '../../core/application.states';
import { CharacteristicTypeRequester } from '../../requesters/core/characteristic-type.requester';
import { DateFormatRequester } from '../../requesters/core/date-format.requester';
import { LanguageRequester } from '../../requesters/core/language.requester';
import { TimezoneRequester } from '../../requesters/core/timezone.requester';
import { IDepotSchemaModel } from '../../requesters/depot/depot.model';
import { DepotRequester } from '../../requesters/depot/depot.requester';
import { CreateEntityFromDepotModalComponent } from '../components/create-entity-from-depot-modal/create-entity-from-depot-modal.component';
import {
	DepotAction,
	DepotActionPopupComponent,
	IDepotActionModalData,
} from '../components/depot-action-popup/depot-action-popup.component';
import { DepotItemEntity } from '../components/depot-list/depot-item.entity';
import {
	DepotSchemaEntity,
	SchemaSelectedHeader,
} from '../components/depot-schema-viewer/depot-schema.entity';
import { AppHeaderMappingModalComponent } from '../components/header-mapping-modal/header-mapping-modal.component';
import { DepotAdapter } from '../depot.adapter';
import { DepotSchemaConfig } from '../domain/depot-schema-config';
import {
	bulkDeleteDepots,
	createEntityFromDepot,
	createEntityFromDepotFail,
	createEntityFromDepotSuccess,
	deleteDepot,
	deleteDepotFail,
	DepotActions,
	fetchDepotHeadersFail,
	fetchDepotHeadersSuccess,
	fetchDepotListFail,
	fetchDepotListSuccess,
	fetchDepotSchemaMappingError,
	fetchDepotSchemaMappingSuccess,
	getDepotHeaderSettingFail,
	getDepotHeaderSettingSuccess,
	initAvailableDepotHeadersForEntityCreation,
	initDepotHeaderSetting,
	initDepotList,
	navigateToDepot,
	navigateToDepotCreate,
	navigateToDepotDuplicate,
	openCreateDepotEntityModal,
	openDeleteConfirmationPopup,
	openDepotHeaderFieldsSelection,
	reconstructDepotSchemaMappingError,
	reconstructDepotSchemaMappingSuccess,
	refreshDepotList,
	toggleDepotStatus,
	triggerDetectAvailableHeaders,
	updateEntitiesCountForDepot,
	updateEntitiesCountForDepotFailure,
	updateEntitiesCountForDepotSuccess,
	updateIncludePublic,
} from './depot.actions';
import { DepotSelector } from './depot.selector';

@Injectable()
export class DepotEffects {
	// ##########################################################
	// Navigation Effects
	// ##########################################################

	public navigateToDepotCreate = createEffect(
		() =>
			this.actions$.pipe(
				ofType(navigateToDepotCreate),
				tap(() => this.stateService.go(states.DepotCreate))
			),
		{
			dispatch: false,
		}
	);

	public navigateToDepot = createEffect(
		() =>
			this.actions$.pipe(
				ofType(navigateToDepot),
				tap((a) =>
					this.stateService.go(states.DepotEdit, {
						id: `${a.id}`,
						type: `${a.depotType}`,
					})
				)
			),
		{
			dispatch: false,
		}
	);

	public navigateToDepotDuplicate = createEffect(
		() =>
			this.actions$.pipe(
				ofType(navigateToDepotDuplicate),
				tap((a) =>
					this.stateService.go(states.DepotDuplicate, {
						duplicatedId: `${a.id}`,
						type: a.depotType,
					})
				)
			),
		{
			dispatch: false,
		}
	);

	// ##########################################################
	// Business Effects
	// ##########################################################

	public initDepotList$ = createEffect(() =>
		this.actions$.pipe(
			ofType(initDepotList, refreshDepotList),
			withLatestFrom(this.depotSelector.includePublic$()),
			switchMap(([action, includePublic]) => {
				if (action.type === DepotActions.RefreshDepotList) {
					return this.depotRequester.getDepotList(includePublic, false);
				}
				return this.depotRequester.getDepotList(includePublic, true);
			}),
			map((response) =>
				fetchDepotListSuccess({
					depotList: this.depotAdapter.generateDepotList(response),
				})
			),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					fetchDepotListFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public getEntitiesCountForDepot$ = createEffect(() =>
		this.actions$.pipe(
			ofType(updateEntitiesCountForDepot),
			switchMap((action) =>
				this.depotRequester.entitiesByDepotBulkCount(
					action.ids,
					!action.shouldReload
				)
			),
			withLatestFrom(this.depotSelector.getDepotList$()),
			map(([response, currentList]) =>
				updateEntitiesCountForDepotSuccess({
					depotList: this.depotAdapter.updateListWithEntityCount(
						currentList,
						response
					),
				})
			),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					updateEntitiesCountForDepotFailure({
						error,
					})
				);
				return caught;
			})
		)
	);

	public updateGetPublic$ = createEffect(() =>
		this.actions$.pipe(
			ofType(updateIncludePublic),
			map(() => refreshDepotList())
		)
	);

	public deleteDepots$ = createEffect(() =>
		this.actions$.pipe(
			ofType(deleteDepot),
			withLatestFrom(this.depotSelector.getDepotList$()),
			switchMap(([action, list]) =>
				this.depotRequester.deleteDepot(
					action.depotId,
					(list.elements.get(action.depotId.toString()) as DepotItemEntity).type
				)
			),
			map(() => {
				this.ngCommService.notifyOnSuccess(
					$localize`:i18n=@@depot.delete.success:`
				);
				return refreshDepotList();
			}),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.ngCommService.notifyOnError(
					$localize`:i18n=@@depot.delete.error:`
				);
				this.store.dispatch(deleteDepotFail());
				return caught;
			})
		)
	);

	public bulkDeleteDepots$ = createEffect(() =>
		this.actions$.pipe(
			ofType(bulkDeleteDepots),
			switchMap((action) => this.depotRequester.deleteMany(action.depotIds)),
			map(() => {
				this.ngCommService.notifyOnSuccess(
					$localize`:i18n=@@depot.delete.multiple.success:`
				);
				return refreshDepotList();
			}),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.ngCommService.notifyOnError(
					$localize`:i18n=@@depot.delete.multiple.error:`
				);
				this.store.dispatch(refreshDepotList());
				return caught;
			})
		)
	);

	public toggleActivationStatus$ = createEffect(() =>
		this.actions$.pipe(
			ofType(toggleDepotStatus),
			switchMap((action) =>
				this.depotRequester.changeActivationStatusDepot(
					action.depotId,
					action.depotType,
					action.status
				)
			),
			map(() => refreshDepotList()),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(refreshDepotList());
				return caught;
			})
		)
	);

	public initAvailableDepotHeadersForEntityCreation$ = createEffect(() =>
		this.actions$.pipe(
			ofType(initAvailableDepotHeadersForEntityCreation),
			switchMap((action) =>
				combineAll({
					availableCharacteristicTypes:
						this.characteristicTypeRequester.getAvailableTypes(),
					availableDateFormats:
						this.dateFormatRequester.getAvailableDateFormats(),
					availableTimezones: this.timezoneRequester.getAvailableTimezones(),
					supportedLanguages: this.languageRequester.getSupportedLanguages(),
					headersForSelectRepo: this.depotRequester.getDepotHeaders(
						action.depotId,
						action.depotType,
						true
					),
					tags: this.tagRequester.getAvailableTags(true),
				})
			),
			map(
				({
					availableCharacteristicTypes,
					availableDateFormats,
					availableTimezones,
					supportedLanguages,
					headersForSelectRepo,
					tags,
				}) =>
					fetchDepotHeadersSuccess({
						headers:
							this.depotAdapter.generateDepotHeaderList(headersForSelectRepo),
						availableCharacteristicTypes:
							this.depotAdapter.generateAvailableTypes(
								availableCharacteristicTypes
							),
						availableDateFormats:
							this.depotAdapter.generateAvailableDateFormats(
								availableDateFormats
							),
						availableTimezones,
						supportedLanguages:
							this.depotAdapter.generateSupportedLanguages(supportedLanguages),
						availableTags: this.tagAdapter.generateTags(
							tags as ReadonlyArray<IDcTag>
						),
					})
			),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					fetchDepotHeadersFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public createEntityFromDepot$ = createEffect(() =>
		this.actions$.pipe(
			ofType(createEntityFromDepot),
			withLatestFrom(
				this.depotSelector.getAvailableCharacteristicTypes$(),
				this.depotSelector.getHeadersForSelectRepo$()
			),
			switchMap(([action, types, list]) =>
				this.depotRequester.createEntityFromDepot(
					action.depotId,
					action.metadata,
					action.headers,
					list.elements.toList().toArray(),
					types.toList().toArray()
				)
			),
			map((identifier) => {
				this.ngCommService.notifyOnSuccess(
					$localize`:i18n=@@depot.modal.entity.create.success:`
				);
				this.ajsScopeService.broadcastOnRootScope(
					'linked-entity:list:refresh',
					identifier
				);

				return createEntityFromDepotSuccess();
			}),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.ngCommService.notifyOnError(
					$localize`:i18n=@@depot.modal.entity.create.error:`
				);
				this.store.dispatch(
					createEntityFromDepotFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public goToDepotListAfterCreate$ = createEffect(() =>
		this.actions$.pipe(
			ofType(createEntityFromDepotSuccess),
			map(() => refreshDepotList()),
			tap(() =>
				this.modalService.closeActiveModal({
					result: 'success',
				})
			)
		)
	);

	public initDepotHeaderSetting$ = createEffect(() =>
		this.actions$.pipe(
			ofType(initDepotHeaderSetting),
			switchMap(() =>
				combineAll({
					availableTypes: this.characteristicTypeRequester.getAvailableTypes(),
					availableDateFormats:
						this.dateFormatRequester.getAvailableDateFormats(),
					availableTimezones: this.timezoneRequester.getAvailableTimezones(),
					supportedLanguages: this.languageRequester.getSupportedLanguages(),
				})
			),
			map(
				({
					availableTypes,
					availableDateFormats,
					availableTimezones,
					supportedLanguages,
				}) =>
					getDepotHeaderSettingSuccess({
						availableCharacteristicTypes:
							this.depotAdapter.generateAvailableTypes(availableTypes),
						availableDateFormats:
							this.depotAdapter.generateAvailableDateFormats(
								availableDateFormats
							),
						availableTimezones,
						supportedLanguages:
							this.depotAdapter.generateSupportedLanguages(supportedLanguages),
					})
			),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					getDepotHeaderSettingFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	// ##########################################################
	// Confirmation popup effects
	// ##########################################################

	public openDeleteConfirmationPopup$ = createEffect(() =>
		this.actions$.pipe(
			ofType(openDeleteConfirmationPopup),
			switchMap((action) => {
				const modalRef = this.modalService.openPopup<IDepotActionModalData>(
					DepotActionPopupComponent,
					{
						title: $localize`:i18n=@@depot.action.popup.delete.header:`,
						subTitle: '',
						depotId: action.depotId,
						element: action.depotLabel,
						action: DepotAction.Delete,
					},
					{
						disableClose: true,
					}
				);
				return combineLatest([of(action), modalRef.afterClosed()]);
			}),
			filter(([, modalResult]) => modalResult !== undefined),
			map(([action]) => {
				if (action.depotId.length === 1) {
					return deleteDepot({
						depotId: action.depotId[0],
					});
				} else {
					return bulkDeleteDepots({
						depotIds: action.depotId,
					});
				}
			})
		)
	);

	public openCreateDepotEntityModal$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(openCreateDepotEntityModal),
				map((action) =>
					this.modalService.openModal<{
						depotId: number;
						depotLabel: string;
						depotType: string;
					}>(
						CreateEntityFromDepotModalComponent,
						{
							title: $localize`:i18n=@@depot.modal.create.entity.header:`,
							subTitle: '',
							depotId: action.depotId,
							depotType: action.depotType,
							depotLabel: action.depotLabel,
						},
						{
							disableClose: true,
						}
					)
				)
			),
		{
			dispatch: false,
		}
	);

	public openDepotHeaderFieldsSelection$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(openDepotHeaderFieldsSelection),
				switchMap((action) => {
					const modalRef = this.modalService.openModal<{
						depotConfig: DepotSchemaConfig;
					}>(
						AppHeaderMappingModalComponent,
						{
							title: $localize`:i18n=@@depot.headers.mapping:`,
							subTitle: action.params.depotType.toUpperCase(),
							depotConfig: action.params,
						},
						{
							disableClose: true,
						}
					);
					return modalRef.afterClosed();
				}),
				map(
					(modalData: {
						depotMapping: Array<DepotSchemaEntity> | null;
						selectedHeaders: Array<SchemaSelectedHeader>;
					}) => {
						if (modalData.depotMapping === null) {
							return null;
						}
						return {
							selectedHeaders: modalData.selectedHeaders,
							depotMapping: this.depotAdapter.generateDepotSchemaHeaderPayload(
								modalData.depotMapping
							),
						};
					}
				),
				tap((payload) => {
					this.ajsScopeService.broadcastOnRootScope('depot:mapping:', payload);
				})
			),
		{
			dispatch: false,
		}
	);

	public fetchAvailableHeader$ = createEffect(() =>
		this.actions$.pipe(
			ofType(openDepotHeaderFieldsSelection),
			switchMap((action) =>
				// perform request before opening modal to prevent transporting data needlessly to the component
				// uses retrieved schema in edit mode
				action.currentSchema
					? of({
							schema: action.currentSchema,
							headers: [],
					  })
					: this.depotRequester.getSchema(action.params)
			),
			map((payload) => {
				const mapping = this.depotAdapter.generateDepotSchemaHeaderMapping(
					payload as IDepotSchemaModel
				);
				return reconstructDepotSchemaMappingSuccess({
					mapping,
				});
			}),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					reconstructDepotSchemaMappingError({
						error,
					})
				);
				return caught;
			})
		)
	);

	public detectAvailableHeader$ = createEffect(() =>
		this.actions$.pipe(
			ofType(triggerDetectAvailableHeaders),
			switchMap((action) =>
				// perform request before opening modal to prevent transporting data needlessly to the component
				this.depotRequester.getSchema(action.params)
			),
			map((payload) => {
				const mapping = this.depotAdapter.generateDepotSchemaHeaderMapping(
					payload as IDepotSchemaModel
				);
				return fetchDepotSchemaMappingSuccess({
					mapping,
				});
			}),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					fetchDepotSchemaMappingError({
						error,
					})
				);
				return caught;
			})
		)
	);

	public constructor(
		private readonly actions$: Actions,
		private readonly depotSelector: DepotSelector,
		private readonly depotRequester: DepotRequester,
		private readonly characteristicTypeRequester: CharacteristicTypeRequester,
		private readonly dateFormatRequester: DateFormatRequester,
		private readonly timezoneRequester: TimezoneRequester,
		private readonly languageRequester: LanguageRequester,
		private readonly tagRequester: TagRequester,
		private readonly depotAdapter: DepotAdapter,
		private readonly tagAdapter: TagAdapter,
		private readonly stateService: AjsStateService,
		private readonly store: Store,
		private readonly modalService: AppModalService,
		private readonly ngCommService: NgCommService,
		private readonly ajsScopeService: AjsScopeService
	) {}
}
